import { useEffect, useRef } from 'react';
import { useConfig } from '../../hooks/useConfig';
/**
 * Validates that all required secrets are present and non-empty
 * @param secrets - The secrets object to validate
 * @returns Array of secret keys that are missing or empty
 */
const validateRequiredSecrets = (secrets) => {
    return Object.entries(secrets)
        .filter(([, value]) => !value || value.trim() === '')
        .map(([key]) => key);
};
/**
 * Validates the Sierra agent token to prevent injection via script src.
 * Only allows URL-safe characters typically used in tokens.
 */
const isValidAgentToken = (token) => {
    if (!token)
        return false;
    // Allow alphanumeric, underscore, and hyphen only
    return /^[A-Za-z0-9_-]+$/.test(token);
};
/**
 * Builds a safe Sierra script URL using a fixed, trusted origin and an encoded token.
 */
const buildSierraScriptUrl = (token, env) => {
    if (!isValidAgentToken(token)) {
        return null;
    }
    const encodedToken = encodeURIComponent(token);
    let url = new URL(`/agent/${encodedToken}/embed`, 'https://sierra.chat');
    if (env !== 'prod') {
        url = new URL(`/agent/${encodedToken}/embed/qa`, 'https://sierra.chat');
    }
    return url.toString();
};
/**
 * Sierra Chat Integration Component
 *
 * This component loads the Sierra chat SDK and makes it available throughout the app.
 * Place this component once at the app root level.
 *
 * The Sierra agent token is automatically retrieved from the application config (sierraAgentToken).
 *
 * @param userData - User data to pass to Sierra as secrets for personalization
 * @param resizable - Enable Sierra's built-in resizing of the chat window (default: true)
 *
 * Sierra provides its own launcher button. To trigger the chat programmatically, call window.sierra?.openChatModal().
 */
export const Sierra = ({ userData, resizable = true }) => {
    const config = useConfig();
    const resolvedAgentToken = config?.sierraAgentToken ?? '';
    const env = config?.env ?? 'prod';
    const scriptRef = useRef(null);
    // Cleanup on unmount only — avoids tearing down Sierra on every re-render
    useEffect(() => {
        return () => {
            if (window.sierra?.cleanup) {
                window.sierra.cleanup();
            }
            const sierraContainer = document.querySelector('[data-sierra-chat-container]');
            if (sierraContainer?.parentNode) {
                sierraContainer.parentNode.removeChild(sierraContainer);
            }
            if (scriptRef.current?.parentNode) {
                scriptRef.current.parentNode.removeChild(scriptRef.current);
                scriptRef.current = null;
            }
            window.sierraConfig = undefined;
            window.sierra = undefined;
        };
    }, []);
    // Initialize Sierra or update secrets/variables when user data changes
    useEffect(() => {
        if (!userData) {
            console.warn('skipping Sierra: no user data');
            return;
        }
        if (!resolvedAgentToken) {
            console.warn('skipping Sierra: no agent token found in config');
            return;
        }
        const hostLocation = window.location.origin;
        // Variables shared between initialization and updates
        const variables = {
            LOCATION: hostLocation,
            PRODUCT_KEY: 'TRGLOBAL',
            LOCALE: userData.locale,
        };
        // Configure Sierra with user data
        const passedData = {
            USER_EMAIL: userData.email,
            USER_EMPLOYEE_ID: userData.employeeId,
            USER_FIRST_NAME: userData.firstName,
            USER_LAST_NAME: userData.lastName,
            EMPLOYER_ID: userData.employerId,
            EMPLOYER_NAME: userData.employerName,
            COUNTRY_CODE: userData.countryCode,
            PREFERRED_LANGUAGE: userData.preferredLanguage,
        };
        // Validate secrets
        const missingOrEmptySecrets = validateRequiredSecrets(passedData);
        if (missingOrEmptySecrets.length > 0) {
            console.error('Sierra: Missing or empty required secrets:', missingOrEmptySecrets);
            return;
        }
        // If Sierra is already initialized, update secrets/variables without tearing down
        if (window.sierra) {
            window.sierra.updateSecrets(passedData).catch(error => {
                console.error('Failed to update Sierra secrets:', error);
            });
            window.sierra.updateVariables(variables).catch(error => {
                console.error('Failed to update Sierra variables:', error);
            });
            return;
        }
        // Prevent duplicate script loading
        if (scriptRef.current) {
            return;
        }
        // Calculate resize dimensions as percentage of viewport (80% of screen size)
        const resizeDimensions = resizable
            ? {
                width: `${Math.round(window.innerWidth * 0.8)}px`,
                height: `${Math.round(window.innerHeight * 0.8)}px`,
            }
            : undefined;
        const sierraConfig = {
            display: 'corner',
            preload: 'timeout',
            launcher: {
                position: 'bottom-right',
                text: '',
            },
            dimensions: {
                width: '30em',
                height: '40em',
            },
            // Use Sierra's built-in resize instead of custom implementation
            // Resize dimensions are larger than default (80% of viewport)
            ...(resizable &&
                resizeDimensions && {
                resize: {
                    display: 'modal',
                    dimensions: resizeDimensions,
                },
            }),
            variables,
            secrets: passedData,
        };
        // Set the config in window before loading the script
        window.sierraConfig = sierraConfig;
        // Load Sierra script
        const agentScript = document.createElement('script');
        agentScript.type = 'module';
        agentScript.async = true;
        agentScript.crossOrigin = 'anonymous';
        const safeSrc = buildSierraScriptUrl(resolvedAgentToken, env);
        if (!safeSrc) {
            return;
        }
        agentScript.src = safeSrc;
        agentScript.onerror = error => {
            console.error('Sierra: Failed to load script:', error);
        };
        document.head.appendChild(agentScript);
        scriptRef.current = agentScript;
    }, [
        resolvedAgentToken,
        resizable,
        userData?.email,
        userData?.employeeId,
        userData?.firstName,
        userData?.lastName,
        userData?.employerId,
        userData?.employerName,
        userData?.countryCode,
        userData?.preferredLanguage,
        userData?.locale,
        env,
    ]);
    return null;
};
